import React, { useState, useEffect } from 'react';
import { Quote, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';

export default function TestimonialCard({ item }) {
  return (
    <div className="bg-white rounded-2xl border border-[#D9E8E5] p-7 sm:p-8 shadow-card flex flex-col justify-between relative overflow-hidden h-full">
      <Quote className="w-10 h-10 text-[#2A9D8F]/20 absolute top-6 right-6" />
      
      <div className="relative z-10 mb-6">
        <p className="font-serif text-lg sm:text-xl text-[#123B5D] font-normal leading-relaxed mt-2 italic">
          "{item.quote}"
        </p>
      </div>

      <div className="pt-4 border-t border-[#D9E8E5] flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-[#123B5D] flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-[#0F766E]" />
            <span>{item.author}</span>
          </h4>
          <p className="text-xs text-[#64748B] mt-0.5">
            {item.relation} &bull; {item.location}
          </p>
        </div>
      </div>
    </div>
  );
}

export function TestimonialSlider({ items }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    if (!isAutoPlaying || !items || items.length === 0) return;
    const timer = setInterval(() => {
      setAnimKey((prev) => prev + 1);
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, 2000); // Display each testimonial for 2 seconds continuously in loop
    return () => clearInterval(timer);
  }, [isAutoPlaying, items]);

  const handleNext = () => {
    setAnimKey((prev) => prev + 1);
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  const handlePrev = () => {
    setAnimKey((prev) => prev + 1);
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const goToSlide = (index) => {
    setAnimKey((prev) => prev + 1);
    setCurrentIndex(index);
  };

  if (!items || items.length === 0) return null;

  const len = items.length;
  const leftIdx = (currentIndex - 1 + len) % len;
  const centerIdx = currentIndex;
  const rightIdx = (currentIndex + 1) % len;

  const leftItem = items[leftIdx];
  const centerItem = items[centerIdx];
  const rightItem = items[rightIdx];

  return (
    <div
      className="max-w-7xl mx-auto relative px-4 py-4"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* 3 Parallel Cards Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center w-full max-w-xl md:max-w-2xl lg:max-w-7xl mx-auto">
        
        {/* Left Card (Dissolving Out / Dimmed) */}
        <div
          key={`${animKey}-left-${leftItem.id}`}
          onClick={() => goToSlide(leftIdx)}
          className="hidden lg:block bg-white/80 rounded-2xl border border-[#D9E8E5] p-6 shadow-card cursor-pointer transition-all duration-700 ease-out hover:opacity-90 opacity-60 scale-95 animate-dissolveLeft"
        >
          <Quote className="w-8 h-8 text-[#2A9D8F]/20 mb-3" />
          <p className="font-serif text-sm text-[#123B5D] font-normal leading-relaxed italic line-clamp-4">
            "{leftItem.quote}"
          </p>
          <div className="mt-4 pt-3 border-t border-[#D9E8E5] flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-[#0F766E] flex-shrink-0" />
            <div>
              <h5 className="text-xs font-bold text-[#123B5D]">{leftItem.author}</h5>
              <p className="text-[10px] text-[#64748B]">{leftItem.location}</p>
            </div>
          </div>
        </div>

        {/* Center Active Card (Dissolving In / Highlighted) */}
        <div
          key={`${animKey}-center-${centerItem.id}`}
          className="bg-white rounded-3xl border border-[#0F766E]/30 p-6 sm:p-8 lg:p-9 shadow-elevated relative z-10 lg:scale-105 transition-all duration-700 ease-out animate-dissolveCenter w-full max-w-xl md:max-w-2xl lg:max-w-none mx-auto"
        >
          <Quote className="w-10 h-10 sm:w-12 sm:h-12 text-[#2A9D8F]/20 absolute top-5 sm:top-6 right-5 sm:right-6 pointer-events-none" />

          <div className="relative z-10">
            <p className="font-serif text-base sm:text-lg lg:text-xl text-[#123B5D] font-normal leading-relaxed italic pr-4">
              "{centerItem.quote}"
            </p>
          </div>

          <div className="mt-6 sm:mt-8 pt-4 sm:pt-5 border-t border-[#D9E8E5] flex items-center justify-between gap-4">
            <div>
              <h4 className="text-sm sm:text-base font-bold text-[#123B5D] flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-[#0F766E] flex-shrink-0" />
                <span>{centerItem.author}</span>
              </h4>
              <p className="text-xs text-[#64748B] mt-0.5">
                {centerItem.relation} &bull; {centerItem.location}
              </p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handlePrev}
                aria-label="Previous Testimonial"
                className="p-2 rounded-full bg-[#F5FAF9] hover:bg-[#0F766E] hover:text-white border border-[#D9E8E5] text-[#123B5D] transition-all shadow-sm active:scale-95"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={handleNext}
                aria-label="Next Testimonial"
                className="p-2 rounded-full bg-[#F5FAF9] hover:bg-[#0F766E] hover:text-white border border-[#D9E8E5] text-[#123B5D] transition-all shadow-sm active:scale-95"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Right Card (Dissolving In / Dimmed) */}
        <div
          key={`${animKey}-right-${rightItem.id}`}
          onClick={() => goToSlide(rightIdx)}
          className="hidden lg:block bg-white/80 rounded-2xl border border-[#D9E8E5] p-6 shadow-card cursor-pointer transition-all duration-700 ease-out hover:opacity-90 opacity-60 scale-95 animate-dissolveRight"
        >
          <Quote className="w-8 h-8 text-[#2A9D8F]/20 mb-3" />
          <p className="font-serif text-sm text-[#123B5D] font-normal leading-relaxed italic line-clamp-4">
            "{rightItem.quote}"
          </p>
          <div className="mt-4 pt-3 border-t border-[#D9E8E5] flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-[#0F766E] flex-shrink-0" />
            <div>
              <h5 className="text-xs font-bold text-[#123B5D]">{rightItem.author}</h5>
              <p className="text-[10px] text-[#64748B]">{rightItem.location}</p>
            </div>
          </div>
        </div>

      </div>

      {/* Dots Indicator */}
      <div className="flex items-center justify-center gap-2.5 mt-8">
        {items.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              idx === currentIndex
                ? 'w-8 bg-[#0F766E] shadow-sm'
                : 'w-2.5 bg-[#0F766E]/20 hover:bg-[#0F766E]/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
